// Which product analytics region, if any, the signed-in user's tenant sends
// to. A tenant opts in with exactly one of the `product_analytics_us` /
// `product_analytics_eu` entitlements, and the dashboard must know before it
// starts the SDK: anything the SDK sends, even the first pageview, would
// already have left the browser, and each region is a separate PostHog project.
//
// `/v1/me` carries the resolved `product_analytics_region`. That RPC needs no
// tenant permission, so every user gets an answer whatever their role, and
// the gate shares the user store's request rather than making its own.
// Anything but US or EU is off: neither entitlement, both, a backend that
// predates the field, a failed or slow read.
//
// The user store is imported when the gate first runs, not with this module,
// because the user store imports this module's region parser.

import { defineEnum } from "./enum.js";

export const ANALYTICS_REGION = defineEnum("ANALYTICS_REGION", {
    US: "us",
    EU: "eu",
});

// The gateway spells proto enums by name; numbers are accepted too.
const REGION_BY_WIRE = new Map([
    ["PRODUCT_ANALYTICS_REGION_US", ANALYTICS_REGION.US],
    [1, ANALYTICS_REGION.US],
    ["PRODUCT_ANALYTICS_REGION_EU", ANALYTICS_REGION.EU],
    [2, ANALYTICS_REGION.EU],
]);

const REGIONS = new Set(Object.values(ANALYTICS_REGION));

// Long enough for a normal read, short enough that a hung request cannot keep
// the dashboard from mounting, since startup waits on the SDK decision.
export const TENANT_GATE_TIMEOUT_MS = 3000;

// The region in a raw `/v1/me` response, or null.
export function analyticsRegionOf(response) {
    const wire = response?.productAnalyticsRegion ?? response?.product_analytics_region;
    return REGION_BY_WIRE.get(wire) ?? null;
}

async function currentUser() {
    const { getCurrentUser } = await import("../userInfo.js");
    return getCurrentUser();
}

export async function tenantAnalyticsRegion({ readUser = currentUser, timeoutMs = TENANT_GATE_TIMEOUT_MS } = {}) {
    let timer;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); });
    try {
        const user = await Promise.race([Promise.resolve().then(readUser), timeout]);
        const region = user?.productAnalyticsRegion;
        return REGIONS.has(region) ? region : null;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}
